import { arrayOf, number, shape, string, bool } from 'prop-types';
import React from 'react';
import { MQ_MIDLARGE, MQ_MEDIUM } from 'constants/mq';
import { imageUrl, hasFileExtension, renderOpsPropTypes } from 'util/images';
import {
  isFixedSize,
  buildSizes,
  generateWidthList,
  generatePixelList,
  HI_DPI_FACTOR
} from 'util/images/responsiveImages';
import { POST_COL_WIDTH } from 'constants/layout';

const MAX_POST_IMG_WIDTH = POST_COL_WIDTH * HI_DPI_FACTOR; // px

// prettier-ignore
const DATA_URI = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// defaults based on the most common Image placement (in posts)
export const DEFAULT_SOURCE_SIZES = [
  {
    media: MQ_MIDLARGE,
    layoutWidth: {
      value: POST_COL_WIDTH,
      units: 'px'
    }
  },
  {
    media: MQ_MEDIUM,
    layoutWidth: {
      value: 696,
      units: 'px'
    }
  },
  {
    layoutWidth: {
      value: 382,
      units: 'px'
    }
  }
];

// again we're basing this on the most common in-post image
export const DEFAULT_MAX_SIZE = {
  width: POST_COL_WIDTH,
  aspectRatio: 4 / 5
};

export const BASE_RENDER_OPS = {
  // we always want to specify 'crop' as default. when a height and width are
  // passed, there is always risk they won't match the image's natural
  // aspect ratio, which means the image will get stretched/distorted.
  resizeMode: 'crop',
  // Safari seems to want to load src and srcset img urls so make sure default render isn't insanely big.
  // If we didn't have to support IE11 we could probably make sure this is the smallest supported mobile width
  // but since we support browsers that don't support srcset we need to make sure that src width
  // is desktop instead of mobile first
  width: MAX_POST_IMG_WIDTH
};

/**
 * returns a responsive <img /> based on the canonical image URL, imageOps, and
 * other props.
 *
 */
function Image(props) {
  const {
    url,
    format,
    alt,
    maxSize: { width, aspectRatio },
    className,
    renderOps,
    sourceSizes,
    shouldTressleIgnore
  } = props;

  const convertToVideo = format === 'gif' || hasFileExtension(url, 'gif');

  // CLEANUP: only apply { resizeMode: crop } if maxSize is specified.
  const finalRenderOps = {
    ...BASE_RENDER_OPS,
    ...renderOps,
    ...(convertToVideo && {
      format: 'mp4',
      width: 720
    })
  };

  const usePixelList = sourceSizes.every(source =>
    isFixedSize(source.layoutWidth.units)
  );

  // If all of the layout widths are fixed size, we can use a simpler source list
  // with specific dimensions
  const srcSet = usePixelList
    ? generatePixelList(url, sourceSizes, finalRenderOps, aspectRatio)
    : generateWidthList(url, finalRenderOps, width, aspectRatio);

  const imgProps = {
    src: DATA_URI,
    srcSet,
    sizes:
      sourceSizes && sourceSizes.length > 0
        ? buildSizes(sourceSizes)
        : undefined
  };

  if (shouldTressleIgnore) {
    imgProps['data-tressle-ignore'] = true;
  }

  return convertToVideo ? (
    <video className={className} autoPlay muted playsInline loop>
      <source src={imageUrl(url, finalRenderOps)} type="video/mp4" />
    </video>
  ) : (
    // Set alt fallback (to handle situations where alt is `null`)
    <img {...imgProps} alt={alt || ''} className={className} />
  );
}

Image.propTypes = {
  url: string.isRequired,
  format: string,
  alt: string,
  className: string,
  maxSize: shape({
    width: number,
    aspectRatio: number
  }),
  renderOps: shape(renderOpsPropTypes),
  sourceSizes: arrayOf(
    shape({
      media: string,
      layoutWidth: shape({
        value: number,
        units: string
      }).isRequired
    })
  ),
  shouldTressleIgnore: bool
};

Image.defaultProps = {
  alt: '',
  format: undefined,
  className: undefined,
  maxSize: DEFAULT_MAX_SIZE,
  renderOps: {},
  sourceSizes: DEFAULT_SOURCE_SIZES,
  shouldTressleIgnore: false
};

export default Image;
